"use client";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { Store, Search, X, ChevronRight, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { LanguageSwitcher } from "./lang-switcher";

interface SubCategory {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
  sub_categories?: SubCategory[];
}

interface GroupCategory {
  sort_order: number;
  category: Category;
}

interface NavigationGroup {
  id: string;
  label: string;
  slug: string;
  sort_order: number;
  navigation_group_categories: GroupCategory[];
}

interface MobileMenuProps {
  navigationGroups: NavigationGroup[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const PALETTE = {
  bg:          "#ffffff",
  bgSoft:      "#fdf9f6",
  border:      "#dbc5ae",
  borderDark:  "#c5a08a",
  text:        "#3b2a20",
  textMid:     "#64483c",
  textLight:   "#8a5c3e",
  accent:      "#7a4e2d",
  accentHover: "#3b2a20",
  accentPale:  "#fad5b2",
  cream:       "#f5e6d6",
};

// View stack: "groups" → "categories" → "subcategories"
type View = "groups" | "categories" | "subcategories";

export default function MobileMenu({ navigationGroups, isOpen, onOpenChange }: MobileMenuProps) {
  const router = useRouter();
  const [view, setView] = useState<View>("groups");
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [mounted, setMounted] = useState(false);

  const activeGroup = navigationGroups.find((g) => g.id === activeGroupId);
  const groupCategories = activeGroup
    ? activeGroup.navigation_group_categories
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((gc) => gc.category)
    : [];

  const activeCategory = groupCategories.find((c) => c.id === activeCategoryId);
  const subs = activeCategory?.sub_categories ?? [];

  // Flat list of all categories across all groups for search
  const allCategories = navigationGroups.flatMap((g) =>
    g.navigation_group_categories
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((gc) => gc.category)
  );

  const filteredCategories = searchQuery.trim()
    ? allCategories.filter(
        (cat) =>
          cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cat.sub_categories?.some((s) =>
            s.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : [];

  const handleClose = () => {
    setMounted(false);
    onOpenChange(false);
    setTimeout(() => {
      setView("groups");
      setActiveGroupId(null);
      setActiveCategoryId(null);
      setSearchQuery("");
    }, 350);
  };

  const navigateTo = (url: string) => {
    handleClose();
    router.push(url);
  };

  const animate = (fn: () => void) => {
    if (isAnimating) return;
    setIsAnimating(true);
    fn();
    setTimeout(() => setIsAnimating(false), 350);
  };

  const drillIntoGroup = (groupId: string) =>
    animate(() => { setActiveGroupId(groupId); setView("categories"); });

  const drillIntoCategory = (categoryId: string) =>
    animate(() => { setActiveCategoryId(categoryId); setView("subcategories"); });

  const drillBack = () => {
    if (view === "subcategories") animate(() => { setActiveCategoryId(null); setView("categories"); });
    else if (view === "categories") animate(() => { setActiveGroupId(null); setView("groups"); });
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setMounted(true), 60);
    } else {
      setMounted(false);
      setTimeout(() => {
        setView("groups");
        setActiveGroupId(null);
        setActiveCategoryId(null);
        setSearchQuery("");
      }, 350);
    }
  }, [isOpen]);

  const panelX = (panelView: View) => {
    const order: View[] = ["groups", "categories", "subcategories"];
    const curr = order.indexOf(view);
    const panel = order.indexOf(panelView);
    if (panel < curr) return "translateX(-100%)";
    if (panel > curr) return "translateX(100%)";
    return "translateX(0)";
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@200;300;400;500;600&display=swap');
        .mm-display { font-family: 'Cormorant Garamond', Georgia, serif; }
        .mm-body    { font-family: 'Jost', sans-serif; }
        @keyframes mm-slide-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes mm-fade { from { opacity: 0; } to { opacity: 1; } }
        .mm-row-enter { opacity: 0; animation: mm-slide-up 0.38s ease forwards; }
        .mm-panel-fade { animation: mm-fade 0.28s ease forwards; }
        .mm-cat-row { position: relative; overflow: hidden; }
        .mm-cat-row::after {
          content: ''; position: absolute; bottom: 0; left: 0;
          height: 1px; width: 0;
          background: linear-gradient(90deg, #7a4e2d, transparent);
          transition: width 0.3s ease;
        }
        .mm-cat-row:hover::after { width: 100%; }
        .mm-cat-row:hover .mm-cat-name { color: #3b2a20; }
        .mm-cat-row:hover .mm-cat-arrow { opacity: 1; transform: translateX(3px); }
        .mm-cat-arrow { opacity: 0.35; transition: opacity 0.2s ease, transform 0.2s ease; color: #7a4e2d; }
        .mm-sub-row { transition: background 0.15s ease, padding-left 0.2s ease; }
        .mm-sub-row:hover { background: #fdf9f6; padding-left: 20px; }
        .mm-sub-row:hover .mm-sub-name { color: #3b2a20; }
        .mm-sub-row:hover .mm-sub-arrow { opacity: 1; }
        .mm-sub-arrow { opacity: 0.25; transition: opacity 0.2s ease; color: #7a4e2d; }
      `}</style>

      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9 sm:h-10 sm:w-10 rounded-lg hover:scale-105 transition-transform text-gray-900 hover:bg-gray-100"
            aria-label="Menu"
          >
            <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
        </SheetTrigger>

        <SheetContent
          side="left"
          className="mm-body w-[320px] sm:w-[400px] p-0 flex flex-col [&>button]:hidden"
          style={{ zIndex: 200, background: PALETTE.bg, borderRight: `1px solid ${PALETTE.border}` }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Main Menu</SheetTitle>
          </SheetHeader>

          {/* ── Header ── */}
{/* ── Header ── */}
<div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${PALETTE.border}` }}>
    <Link href="/" onClick={handleClose}>
        <Image src="/logo-png.png" alt="Silver & Gems" width={120} height={40} className="h-8 w-auto" priority />
    </Link>
    <div className="flex items-center gap-2">
        <LanguageSwitcher />
        <button onClick={handleClose} className="p-1.5 rounded-full transition-all" style={{ color: PALETTE.borderDark }}>
            <X className="w-4 h-4" />
        </button>
    </div>
</div>

          {/* ── Search ── */}
          <div className="px-5 pt-4 pb-3 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: PALETTE.borderDark }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (view !== "groups") animate(() => setView("groups"));
                }}
                placeholder="Search collections..."
                className="mm-body w-full pl-9 pr-8 py-2.5 text-xs rounded-xl outline-none transition-all"
                style={{ background: PALETTE.bgSoft, border: `1px solid ${PALETTE.border}`, color: PALETTE.text, letterSpacing: "0.06em" }}
                onFocus={(e) => (e.currentTarget.style.borderColor = PALETTE.textMid)}
                onBlur={(e) => (e.currentTarget.style.borderColor = PALETTE.border)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: PALETTE.borderDark }}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* ── Shop All CTA ── */}
          {!searchQuery && (
            <div className="px-5 pb-4 flex-shrink-0">
              <button
                onClick={() => navigateTo("/shop")}
                className="mm-body w-full flex items-center justify-center gap-2 py-2.5 rounded-xl transition-all active:scale-[0.98]"
                style={{ background: PALETTE.accent, color: "#fff", fontSize: "10px", fontWeight: 600, letterSpacing: "0.2em" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = PALETTE.accentHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = PALETTE.accent)}
              >
                <Store className="w-3.5 h-3.5" />
                SHOP ALL PRODUCTS
              </button>
            </div>
          )}

          {/* ── Sliding Panels ── */}
          <div className="flex-1 overflow-hidden relative">

            {/* ── PANEL: Search Results ── */}
            {searchQuery && (
              <div className="absolute inset-0 overflow-y-auto z-10" style={{ background: PALETTE.bg }}>
                <div className="px-5 py-3">
                  <span className="mm-body text-[9px] tracking-[0.2em] uppercase" style={{ color: PALETTE.textLight }}>
                    Results for "{searchQuery}"
                  </span>
                </div>
                {filteredCategories.length === 0 ? (
                  <div className="px-5 py-8 text-center">
                    <span className="mm-body text-xs" style={{ color: PALETTE.textLight }}>No results found</span>
                  </div>
                ) : (
                  filteredCategories.map((category, idx) => {
                    const matchingSubs = category.sub_categories?.filter((s) =>
                      s.name.toLowerCase().includes(searchQuery.toLowerCase())
                    ) ?? [];
                    return (
                      <div key={category.id} className="mm-row-enter" style={{ animationDelay: `${idx * 40}ms` }}>
                        <div
                          className="mm-cat-row flex items-center gap-4 px-5 py-4 cursor-pointer"
                          style={{ borderBottom: `1px solid ${PALETTE.cream}` }}
                    onClick={() => navigateTo(`/shop-category/${category.id}`)}
                        >
                          <span className="mm-cat-name mm-display flex-1 font-light" style={{ color: PALETTE.textMid, fontSize: "17px" }}>
                            {category.name}
                          </span>
                          <ChevronRight className="mm-cat-arrow w-3.5 h-3.5" />
                        </div>
                        {matchingSubs.length > 0 && (
                          <div className="pl-14 pr-5 pb-2">
                            {matchingSubs.map((sub) => (
                              <button
                                key={sub.id}
                                onClick={() => navigateTo(`/shop/${category.id}/${sub.id}`)}
                                className="mm-body w-full text-left py-1.5 text-xs transition-colors"
                                style={{ color: PALETTE.textLight, letterSpacing: "0.05em" }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.text)}
                                onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.textLight)}
                              >
                                <span style={{ color: PALETTE.border, marginRight: "8px" }}>↳</span>
                                {sub.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ── PANEL A: Nav Groups ── */}
            <div
              className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out"
              style={{ transform: searchQuery ? "translateX(-100%)" : panelX("groups") }}
            >
              <div className="px-5 pb-3 pt-1 flex items-center gap-3">
                <span className="mm-body text-[9px] tracking-[0.22em] uppercase flex-shrink-0" style={{ color: PALETTE.textLight }}>
                  Collections
                </span>
                <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${PALETTE.border}, transparent)` }} />
              </div>
              <div className="pb-10">
                {navigationGroups
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((group, idx) => (
                    <div key={group.id} className="mm-row-enter" style={{ animationDelay: mounted ? `${idx * 40}ms` : "0ms" }}>
                      <div
                        className="mm-cat-row flex items-center gap-4 px-5 py-4 cursor-pointer"
                        style={{ borderBottom: `1px solid ${PALETTE.cream}` }}
                        onClick={() => drillIntoGroup(group.id)}
                      >
                        <span className="mm-body text-[10px] tabular-nums flex-shrink-0 w-5 text-right" style={{ color: PALETTE.border, fontWeight: 500 }}>
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="mm-cat-name mm-display flex-1 font-light transition-colors" style={{ color: PALETTE.textMid, fontSize: "17px", letterSpacing: "0.02em" }}>
                          {group.label}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="mm-body text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: PALETTE.accentPale, color: PALETTE.textLight, border: `1px solid ${PALETTE.border}`, letterSpacing: "0.08em" }}>
                            {group.navigation_group_categories.length}
                          </span>
                          <ChevronRight className="mm-cat-arrow w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* ── PANEL B: Categories within group ── */}
            <div
              className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out"
              style={{ transform: searchQuery ? "translateX(100%)" : panelX("categories"), background: PALETTE.bg }}
            >
              {/* Back button */}
              <div className="sticky top-0 z-10" style={{ background: `${PALETTE.bg}ee`, backdropFilter: "blur(10px)", borderBottom: `1px solid ${PALETTE.border}` }}>
                <button
                  onClick={drillBack}
                  className="mm-body flex items-center gap-2 px-5 py-4 w-full transition-colors"
                  style={{ color: PALETTE.textLight, fontSize: "10px", letterSpacing: "0.18em" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.textLight)}
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  ALL COLLECTIONS
                </button>
              </div>

              {activeGroup && (
                <div className="mm-panel-fade">
                  <div className="px-6 pt-5 pb-4" style={{ borderBottom: `1px solid ${PALETTE.border}` }}>
                    <p className="mm-body text-[9px] tracking-[0.22em] uppercase mb-1" style={{ color: PALETTE.textLight }}>Group</p>
                    <h2 className="mm-display font-light" style={{ color: PALETTE.text, fontSize: "clamp(22px, 6vw, 28px)" }}>
                      {activeGroup.label}
                    </h2>
                  </div>
                  <div className="pb-10">
                    {groupCategories.map((category, idx) => {
                      const hasSubs = (category.sub_categories?.length ?? 0) > 0;
                      return (
                        <div key={category.id} className="mm-row-enter" style={{ animationDelay: `${idx * 40}ms` }}>
                          <div
                            className="mm-cat-row flex items-center gap-4 px-5 py-4 cursor-pointer"
                            style={{ borderBottom: `1px solid ${PALETTE.cream}` }}
                            onClick={() =>
                              hasSubs
                                ? drillIntoCategory(category.id)
                              : navigateTo(`/shop-category/${category.id}`)
                            }
                          >
                            <span className="mm-body text-[10px] tabular-nums flex-shrink-0 w-5 text-right" style={{ color: PALETTE.border, fontWeight: 500 }}>
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                            <span className="mm-cat-name mm-display flex-1 font-light transition-colors" style={{ color: PALETTE.textMid, fontSize: "17px", letterSpacing: "0.02em" }}>
                              {category.name}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasSubs && (
                                <span className="mm-body text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: PALETTE.accentPale, color: PALETTE.textLight, border: `1px solid ${PALETTE.border}`, letterSpacing: "0.08em" }}>
                                  {category.sub_categories?.length}
                                </span>
                              )}
                              <ChevronRight className="mm-cat-arrow w-3.5 h-3.5" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ── PANEL C: Subcategories ── */}
            <div
              className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out"
              style={{ transform: searchQuery ? "translateX(100%)" : panelX("subcategories"), background: PALETTE.bg }}
            >
              <div className="sticky top-0 z-10" style={{ background: `${PALETTE.bg}ee`, backdropFilter: "blur(10px)", borderBottom: `1px solid ${PALETTE.border}` }}>
                <button
                  onClick={drillBack}
                  className="mm-body flex items-center gap-2 px-5 py-4 w-full transition-colors"
                  style={{ color: PALETTE.textLight, fontSize: "10px", letterSpacing: "0.18em" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.textLight)}
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  BACK TO {activeGroup?.label ?? "CATEGORIES"}
                </button>
              </div>

              {activeCategory && (
                <div className="mm-panel-fade">
                  <div className="px-6 pt-7 pb-6" style={{ borderBottom: `1px solid ${PALETTE.border}` }}>
                    <p className="mm-body text-[9px] tracking-[0.22em] uppercase mb-2" style={{ color: PALETTE.textLight }}>Collection</p>
                    <h2 className="mm-display font-light leading-none mb-3" style={{ color: PALETTE.text, fontSize: "clamp(26px, 7vw, 32px)", letterSpacing: "-0.01em" }}>
                      {activeCategory.name}
                    </h2>
                    <div style={{ height: "2px", width: "40px", background: `linear-gradient(90deg, ${PALETTE.accent}, ${PALETTE.accentPale})`, borderRadius: "2px", marginBottom: "14px" }} />
                    <button
                  onClick={() => navigateTo(`/shop-category/${activeCategory.id}`)}
                      className="mm-body inline-flex items-center gap-1 transition-colors"
                      style={{ color: PALETTE.accent, fontSize: "10px", letterSpacing: "0.16em" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = PALETTE.accentHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = PALETTE.accent)}
                    >
                      VIEW ENTIRE COLLECTION
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="px-6 pt-5 pb-2">
                    <span className="mm-body text-[9px] tracking-[0.2em] uppercase" style={{ color: PALETTE.textLight }}>
                      {subs.length} Subcategories
                    </span>
                  </div>

                  <div className="px-4 pb-12">
                    {subs.map((sub, i) => (
                      <div key={sub.id} className="mm-row-enter" style={{ animationDelay: `${i * 30}ms` }}>
                        <button
                          className="mm-sub-row mm-body w-full flex items-center gap-4 px-3 py-3.5 text-left rounded-lg"
                          style={{ borderBottom: `1px solid ${PALETTE.cream}` }}
                          onClick={() => navigateTo(`/shop/${activeCategory.id}/${sub.id}`)}
                        >
                          <span className="mm-body text-[10px] tabular-nums flex-shrink-0 w-5 text-right" style={{ color: PALETTE.border, fontWeight: 500 }}>
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="mm-sub-name mm-display flex-1 font-light transition-colors" style={{ color: PALETTE.textMid, fontSize: "15px", letterSpacing: "0.02em" }}>
                            {sub.name}
                          </span>
                          <ChevronRight className="mm-sub-arrow w-3.5 h-3.5 flex-shrink-0" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="flex-shrink-0 px-6 py-3 flex items-center justify-end" style={{ borderTop: `1px solid ${PALETTE.border}`, background: PALETTE.bgSoft }}>
            <span className="mm-display text-sm italic" style={{ color: PALETTE.textLight, letterSpacing: "0.04em" }}>
              Silver &amp; Gems
            </span>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}